import { Component, computed, input, output, signal, TemplateRef } from '@angular/core';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { SynSpinnerComponent } from '@synergy-design-system/angular';

export interface RowContext<ValueT> {
  $implicit: ValueT;
}

export interface LazyLoadEvent {
  offset: number;
}

export interface ColumnDescription<ValueT> {
  label: string;
  getField: (value: ValueT) => string;
  style?: string;
}

@Component({
  selector: 'app-table-2',
  template: `
    <table class="syn-table--alternating">
      <thead>
        <tr>
          <ng-container [ngTemplateOutlet]="headerTemplate()"> </ng-container>
        </tr>
      </thead>
      <tbody [ngClass]="{ clickable: clickable() }">
        @if (loading()) {
          <td>
            <syn-spinner style="font-size: 20px; --track-width: 4px;"></syn-spinner>
          </td>
        } @else {
          @for (value of values(); track $index) {
            <ng-container [ngTemplateOutlet]="rowTemplate()" [ngTemplateOutletContext]="{ $implicit: value }">
            </ng-container>
          }
        }
      </tbody>
    </table>
    @if (showPagination()) {
      <div class="float-right">
        <div class="flex-row gap-2" style="margin-top: 1rem; margin-bottom: 1rem">
          <div class="flex-row" style="gap: 0.4rem">
            <span>{{ pageStart() }} - {{ pageEnd() }}</span>
            <span i18n="@@of">of</span>
            <span>{{ totalValuesCount() }}</span>
          </div>
          <span class="navigation" (click)="gotoFirstPage()">❮❮</span>
          <span class="navigation" (click)="gotoPrevPage()">❮</span>
          <span class="navigation" (click)="gotoNextPage()">❯</span>
          <span class="navigation" (click)="gotoLastPage()">❯❯</span>
        </div>
      </div>
    }
  `,
  styles: [
    `
      table {
        width: 100%;
      }

      .navigation {
        min-width: 1rem;
      }

      .navigation:hover {
        cursor: pointer;
      }

      :host ::ng-deep tbody.clickable tr:hover td {
        background-color: var(--syn-color-neutral-100);
        cursor: pointer;
      }
    `,
  ],
  imports: [NgClass, NgTemplateOutlet, SynSpinnerComponent],
})
export class Table2Component<ValueT> {
  values = input([] as ValueT[]);
  // How many values should be displayed per page
  pageSize = input(10);
  totalValuesCount = input(0);

  // Make the rows clickable and highlight them on hover
  clickable = input(false);
  selectValue = output<ValueT>();

  onLazyLoad = output<LazyLoadEvent>();

  // show loading spinner
  loading = input(false);

  showPagination = input(true);

  headerTemplate = input.required<TemplateRef<null>>();
  rowTemplate = input.required<TemplateRef<RowContext<ValueT>>>();

  offset = signal(0);
  pageStart = computed(() => {
    if (this.totalValuesCount() === 0) return 0;
    return this.offset() + 1;
  });
  pageEnd = computed(() => {
    return Math.min(this.totalValuesCount(), this.offset() + this.pageSize());
  });

  gotoPrevPage() {
    let offset = this.offset();
    offset -= this.pageSize();
    if (offset < 0) {
      offset = 0;
    }
    this.offset.set(offset);

    this.onLazyLoad.emit({ offset });
  }

  gotoNextPage() {
    let offset = this.offset();
    offset += this.pageSize();
    if (offset >= this.totalValuesCount()) {
      offset = 0;
    }
    this.offset.set(offset);

    this.onLazyLoad.emit({ offset });
  }

  gotoFirstPage() {
    this.offset.set(0);

    this.onLazyLoad.emit({ offset: 0 });
  }

  gotoLastPage() {
    const chunkSize = this.pageSize();
    const lastPageIndex = Math.floor(this.totalValuesCount() / chunkSize);
    const offset = lastPageIndex * chunkSize;
    this.offset.set(offset);

    this.onLazyLoad.emit({ offset });
  }

  onRowClick(value: ValueT) {
    this.selectValue.emit(value);
  }
}
